const toArray = (v) => (Array.isArray(v) ? v : []);
const round = (n) => Math.round(Number(n) || 0);

const buildServices = (procedures) =>
  procedures.map((p) => ({
    service_id: p.id,
    code: p.procedure_code || null,
    name: p.name || null
  }));

export const buildDrClearCareEstimate = ({ benefits, invoice } = {}) => {
  if (!benefits) return null;

  const acc = benefits.accumulators || {};
  const procedures = toArray(invoice?.procedures).filter((p) => p && p.charge != null);
  const providerCharges = procedures.reduce((sum, p) => sum + round(p.charge), 0);
  const services = buildServices(procedures);

  if (benefits.vertical === 'dental') {
    const insurancePays = procedures.reduce((sum, p) => sum + round(p.ins_payment), 0);
    const ptBalanceSum = procedures.reduce((sum, p) => sum + round(p.pt_balance), 0);
    const patientResponsibility = invoice?.balance != null ? round(invoice.balance) : ptBalanceSum;

    const coverageByService = benefits.coverageByService || {};
    const dentalServices = services.map((service) => ({
      ...service,
      dentalCategory: coverageByService[service.service_id]?.dental_category ?? null,
      coveragePercent: coverageByService[service.service_id]?.coverage_percent ?? null
    }));

    return {
      vertical: 'dental',
      coverageActive: true,
      services: dentalServices,
      providerCharges,
      insurancePays,
      patientResponsibility,
      remainingDeductible: acc.remainingDeductible ?? null,
      annualMaxRemaining: acc.annualMaxRemaining ?? null
    };
  }

  const costShare = benefits.cost_share;
  if (!costShare) return null;

  const allowed = procedures.reduce((sum, p) => {
    const amount = p.allowed != null ? p.allowed : p.ins_payment != null ? p.ins_payment : p.charge;
    return sum + round(amount);
  }, 0);
  const writeOff = Math.max(0, providerCharges - allowed);

  // Source the cost-share breakdown from the invoice the backend already applied
  // (invoice.last_applied) so the patient-facing total matches what they're charged.
  // Fall back to the cost_share definition / accumulators only when last_applied is
  // absent, rather than re-deriving the split here (which drifted from the invoice).
  const applied = invoice?.last_applied || {};

  const coinsuranceRate =
    applied.co_insurance?.percentage != null
      ? round(applied.co_insurance.percentage)
      : round((Number(costShare.co_insurance) || 0) / 100); // persisted ×100

  const copay =
    applied.co_payment?.value != null
      ? round(applied.co_payment.value)
      : round(costShare.co_payment);
  const deductible =
    applied.deductible?.value != null
      ? round(applied.deductible.value)
      : Math.max(
          0,
          round(acc.remainingDeductible != null ? acc.remainingDeductible : costShare.deductible)
        );
  const coinsurance = applied.co_insurance?.value != null ? round(applied.co_insurance.value) : 0;

  const ptBalanceSum = procedures.reduce((sum, p) => sum + round(p.pt_balance), 0);
  const patientResponsibility =
    invoice?.balance != null
      ? round(invoice.balance)
      : copay + deductible + coinsurance || ptBalanceSum;

  return {
    vertical: 'medical',
    coverageActive: true,
    services,
    providerCharges,
    allowed,
    writeOff,
    copay,
    deductible,
    coinsurance,
    coinsuranceRate,
    patientResponsibility,
    benefitSummary: {
      deductible: {
        annual: acc.deductibleAnnual ?? null,
        remaining: acc.remainingDeductible ?? null
      },
      outOfPocket: {
        annual: acc.oopMax ?? null,
        remaining: acc.oopRemaining ?? null
      }
    }
  };
};
